import { findUnusedButtonChannel } from "./findUnusedButtonChannel";
import { copyAllProperties } from "./utils";
import Connection, { ConnectionType } from "./Connection";
import { ChannelType, channelTypeInfo, DeviceType } from "./ChannelType";
import Device from "./Device";
import type Page from "./Page";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import DeviceTreeViewModel from "../modelTreeViews/DeviceTreeviewModel";
import Room from "./Room";
import BaseObject from "./BaseObject";
import ConnectableObject from "./ConnectableObject";
import Channel, { ButtonSide } from "./Channel";

import ButtonChannel, { mergeSingleButtonsToRocker, splitRockerToSingleButtons } from "./ButtonChannel";
import { toggleButtonChannelSplit } from "./buttonChannelActions";
import BaseModelMap from "./BaseModelMap";
import { ValueLevels } from "./KnxInstanceData";
import Logger from "../Logger";
import type Floor from "./Floor";

const logger = new Logger();

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import { RockerConfig } from "./KnxRules";
import KnxRule from "./KnxRule";
import { DragMode, DropResult } from "./TreeviewModel";
import Consumer from "./Consumer";
import { CableType } from "./CableType";
import { TAG_TEMPERATUR } from "./Constants";
import { PanelCells } from "../components/PanelLayout/PanelLayoutTypes";
import { DeviceClasses } from "./DeviceClasses";
import { isDevelopmentEnvironment } from "../estada-config";

ObjectFactory.registerClass("Button", (data?: any, parent?: BaseModel) => {
  return new Button(data, parent);
});

//////////////////////////////////////////////////
export default class Button extends Device {
  public get className(): string {
    return "Button"; // no itx
  }
  _classification = DeviceClasses.Button;
  public get isButton(): boolean {
    return true;
  }
  public get isSensor(): boolean {
    if (this.classification == DeviceClasses.SensorNew) {
      return true;
    }

    if (this.image.includes("Praesenzmelder.png")) {
      this.classification = DeviceClasses.SensorNew;
      return true;
    }
    return false;
  }
  public get isDecentralActor(): boolean {
    if (this.classification == DeviceClasses.DecentralActor) {
      return true;
    }

    if (this.image.includes("actor")) {
      this.classification = DeviceClasses.DecentralActor;
      return true;
    }
    return false;
  }

  getParentCollectionName(): string | undefined {
    return "buttons"; // no itx
  }

  // -----------------------------------------------------------------------------
  // rockerConfigs
  // -----------------------------------------------------------------------------
  _rockerConfigs?: Array<RockerConfig>;
  get rockerConfigs(): Array<RockerConfig> | undefined {
    return this._rockerConfigs;
  }

  set rockerConfigs(newValue: Array<RockerConfig> | undefined) {
    if (this._rockerConfigs === newValue) return;
    this.beforeChange("rockerConfigs");
    this._rockerConfigs = newValue;
    this.changed("rockerConfigs");
  }

  // -----------------------------------------------------------------------------

  getPersistentProperties(): string[] {
    return [
      "panelCells", // no itx
      "panelPageLayouts", // no itx
      "rockerConfigs", // no itx
      ...super.getPersistentProperties(),
    ];
  }

  // -----------------------------------------------------------------------------
  // panelCells
  // -----------------------------------------------------------------------------
  _panelCells?: PanelCells;
  get panelCells(): PanelCells | undefined {
    return this._panelCells;
  }

  set panelCells(newValue: PanelCells | undefined) {
    if (this._panelCells === newValue) return;
    this.beforeChange("panelCells");
    this._panelCells = newValue;
    this.changed("panelCells");
  }

  // -----------------------------------------------------------------------------
  // panelPageLayouts
  // -----------------------------------------------------------------------------
  _panelPageLayouts?: {
    [key: number]: { templateId: string };
  };
  get panelPageLayouts():
    | {
        [key: number]: { templateId: string };
      }
    | undefined {
    return this._panelPageLayouts;
  }

  set panelPageLayouts(
    newValue:
      | {
          [key: number]: { templateId: string };
        }
      | undefined,
  ) {
    if (this._panelPageLayouts === newValue) return;
    this.beforeChange("panelPageLayouts");
    this._panelPageLayouts = newValue;
    this.changed("panelPageLayouts");
  }

  // -----------------------------------------------------------------------------

  ////////////////////////////////////////////////////////////////

  drawingItemColor(): string {
    const drawingItemColors = state.settings.drawingItemColors;
    return drawingItemColors.button;
  }

  // non persistent properties
  showChannels: boolean = false;

  get hasActorChannels(): boolean {
    return this.buttonChannels.findFirst((channel) => channel.isActorChannel) != undefined;
  }

  get defaultImageSrc(): string {
    if (this.isDecentralActor) {
      return state.hostBaseUrl + "images/buttons/touch-actor.png"; // no itx
    }

    if (this.isSensor) {
      return state.hostBaseUrl + "images/consumer/Praesenzmelder.png";
    }

    return state.hostBaseUrl + "images/buttons/touch-6.png"; // no itx
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new DeviceTreeViewModel(this);
    this._collapseMode = 0;
  }

  isConnectable(type: ConnectionType, partnerObject: ConnectableObject): boolean {
    return true;
  }
  getImage(): string | undefined {
    return this.image || state.hostBaseUrl + "images/buttons/touch-6.png"; // no itx
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;
    this.collapseMode = 0;
    return super.delete();
  }

  setSelected(newValue: boolean) {
    super.setSelected(newValue);
    this.updateConnectionEndpoints();
  }
  findConnectableChannel(
    connectionType: ConnectionType,
    partnerObject: ConnectableObject,
    rule?: "hard" | "soft", // no itx
  ): Channel | undefined {
    var res: Channel | undefined;
    if (connectionType == ConnectionType.Channel || partnerObject.isActorChannel) {
      return undefined;
    }
    if (partnerObject.className == "Consumer" && channelTypeInfo((partnerObject as Consumer).channelType)?.isSupply) {
      return undefined;
    }
    res = findUnusedButtonChannel(this, partnerObject, connectionType);

    if (res == undefined) {
      //&& this.templateId == "") {
      res = this.createNewRequestChannel();
      res.isNewCreatedFromAutoconnect = true;
      if (this.templateId != "") {
        res.flagErrors({ noMatchingKNXChannel: "noMatchingKNXChannel" }, true);
      }
    }

    logger.log("findConnectableChannelButton", res);
    return res;
  }

  autoConnectChannel(page: Page) {
    const cabinet = this.room?.cabinet;
    //cabinet?.autoConnectChannel(page, this);
    logger.error("autoconnect button with channel isn`t allowed");
  }

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    const connetedChannel = this.getConnectedChannel();
    connetedChannel?.raiseOnChange("consumer");
    super.raiseOnChange(changedProperty, eventSource, uiOnly);
  }

  applySceneConnectionsToKnxParameters(): { newEvalPhaseNeeded: boolean } {
    if (this.template) {
      this.knxButtonChannels.forEach((knxChannel) => {
        const connectedScene = knxChannel.getConnectedScene();
        if (knxChannel.channelType == ChannelType.Scene) {
          if (!knxChannel.knxButtonSceneNumberProperty) {
            logger.error(`No knxButtonSceneNumberProperty for channel: ${knxChannel.name}`);
          } else {
            this.knxInstanceData.setExtendedData(
              { mbe: { isSet: true }, scene: connectedScene },
              knxChannel.knxButtonSceneNumberProperty.id,
              knxChannel.knxButtonSceneNumberProperty.instanceId,
            );

            logger.log(
              `Button: applySceneConnectionsToKnxParameters:   ${knxChannel.knxButtonSceneNumberProperty.id}  `,
            );

            var nr = "-1";
            if (connectedScene) {
              nr = `${Number(connectedScene.sceneNumber) + (this.template?.knxProductData.sceneNumberOffset || 0)}`;
            }

            const changed = this.knxInstanceData.setValue(
              ValueLevels.device,
              nr,
              knxChannel.knxButtonSceneNumberProperty.id,
              knxChannel.knxButtonSceneNumberProperty.instanceId,
              knxChannel.knxButtonSceneNumberProperty.moduleId,
            );
          }
        }
      });
    }

    return { newEvalPhaseNeeded: false };
  }

  /////////////////////////
  // property

  get buttonChannels(): BaseModelMap<ButtonChannel> {
    return this.channels as BaseModelMap<ButtonChannel>;
  }

  get knxButtonChannels(): BaseModelMap<ButtonChannel> {
    return this.knxChannels as BaseModelMap<ButtonChannel>;
  }

  get filterdButtonChannels(): Array<ButtonChannel> {
    return this.buttonChannels.findAsArray(
      (channel) =>
        channel.channelType != ChannelType.General &&
        (channel.channelType != ChannelType.Unknown || !channel.knxChannel) &&
        channel.channelType != ChannelType.Environment,
    );
  }

  /////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    var res = new Array<BaseModel>();
    res = super.getExtendedCopyCollection(startItem);
    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    return (await this.parent?.pasteItem(params)) || [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    const room = parent as Room;
    const page = room.page;
    var undoObjects: Array<BaseModel> = await super.executePaste({
      ...params,
      page: page,
    });
    this.collapseMode = 0;

    if (page) {
      if (isCopyRootObj) {
        page.addButton(this);
        super.updatePageUsageAfterCopy();
        this.updateNameInPaste(room.buttons);
      }
      if (isCopyRootObj || state.projectMetadata.uniqueTagsForProject) {
        this.updateTagInPaste(room.buttons);
      }
    }
    return undoObjects;
  }

  get someChannelOrConnectionIsSelected() {
    let res = false;
    this.channels.forEach((channel) => {
      if (channel.selected) {
        res = true;
      }
      channel.connections.forEach((connection) => {
        if (connection.selected) {
          res = true;
        }
      });
    });
    return res;
  }

  findFreeButtonTag(): { tag: string; index: number } {
    let index = 1;
    let sIndex = "Txx"; // no itx

    let freeButtonFound = false;
    while (!freeButtonFound) {
      sIndex = "T" + ("0" + index).slice(-2);

      freeButtonFound = !(this.channels.getItemByTag(sIndex) || this.channels.getItemByTag(sIndex + "L"));

      if (!freeButtonFound) {
        index++;
      }
    }

    return { tag: sIndex, index: index };
  }
  findFreeActorButtonTag(): { tag: string; index: number } {
    let index = 1;
    let sIndex = "Axx"; // no itx

    let freeButtonFound = false;
    while (!freeButtonFound) {
      sIndex = "A" + ("0" + index).slice(-2);

      freeButtonFound = !this.channels.getItemByTag(sIndex);

      if (!freeButtonFound) {
        index++;
      }
    }

    return { tag: sIndex, index: index };
  }

  createNewRequestChannel(isDali?: boolean, type?: "Button" | "Actor"): Channel {
    const { tag, index } = type === "Actor" ? this.findFreeActorButtonTag() : this.findFreeButtonTag();
    // if button is decentral actor createNewRequestChannel creates ActorChannel instead of ButtonChannel
    let channel: Channel;
    if (type === "Actor") {
      channel = new Channel({ tag: tag, name: "", index: index, isRequestChannel: true }, this);
    } else {
      channel = new ButtonChannel({ tag: tag, name: "", index: index, isRequestChannel: true }, this);
    }

    channel.channelType = ChannelType.Generic;
    this.channels.addWithoutUpdate(channel);

    return channel;
  }

  createNewRequestActorChannel(): Channel {
    const { tag, index } = this.findFreeActorButtonTag();

    let channel: Channel;
    channel = new Channel({ tag: tag, name: "", index: index, isRequestChannel: true }, this);

    channel.channelType = ChannelType.Generic;
    this.channels.addWithoutUpdate(channel);

    return channel;
  }

  //
  // fixButtonChannelTags benennet die "ALTEN" Channeltags "01,02,03, ..."
  // in neue ChannelTags "T01" etc um
  //
  fixButtonChannelTags(isUpgradeMode: boolean, knxChannels: Array<Channel>) {
    const allButtonChannels = this.buttonChannels.sortBy("tag");
    let index = 1;
    allButtonChannels.forEach((buttonChannel) => {
      if (buttonChannel.channelType == ChannelType.TemperatureMeasurement) {
        //
        // MWE 7.4.2025
        // Make sure Tag for TemperatureMeasurement is always TAG_TEMPERATUR
        //
        if (buttonChannel.tag !== TAG_TEMPERATUR) {
          buttonChannel.tag = TAG_TEMPERATUR; // no itx
        }
      } else if (buttonChannel.knxChannel && buttonChannel.knxChannel?.name.toLocaleLowerCase().indexOf("patch") >= 0) {
        //
        // convert patch channels to TP
        //

        buttonChannel.tag = "TP"; // no itx
      } else {
        if (isUpgradeMode) {
          buttonChannel.tag = "T" + buttonChannel.tag; // no itx
        }
      }
    });

    const buttonChannelsToFix = allButtonChannels.filter((c) => c.tag.substring(0, 1) != "T");

    const rules = this.template?.knxProductData.currentRules;

    function fixDuplicateChannelTags(button: Button) {
      const sortedChannels = button.buttonChannels.sortBy("tag");
      let fixIndex = sortedChannels.length;

      function tagsForbidden(tag1: string, tag2: string): boolean {
        let res = false;
        if (tag1 == tag2) {
          return true;
        } else if (tag1 == tag2 + "R" || tag1 == tag2 + "L" || tag2 == tag1 + "L") {
          //
          // mwe 6.3.2025 wir erlauben T01 / T01R gleichberechtigt mit T01L / T01R
          // da manche Taster für die linke Taste die gleichen Namen verwenden wie für eine DoppelTaste (zB: ABB SBB/U)
          //  || tag2 == tag1 + "R"  ist nicht mehr verboten
          //
          return true;
        }
        return res;
      }

      for (let i = 0; i < sortedChannels.length - 1; i++) {
        const channel = sortedChannels[i];

        let j = i + 1;

        let nextChannel = sortedChannels[j++];

        while (tagsForbidden(channel.tag, nextChannel.tag) && j < sortedChannels.length) {
          //
          // doppelter Channel name
          // fix it
          //
          nextChannel.tag = `T${fixIndex++}`;
          nextChannel = sortedChannels[j++];
        }
      }
    }

    buttonChannelsToFix.forEach((buttonChannel) => {
      if (buttonChannel.connections.size == 0) {
        if (buttonChannel.tag != "@I" && buttonChannel.signals.size == 0) {
          //
          // Keep imported Channels & Signals!  (readback from knxNetwork)
          //
          buttonChannel.delete();
        }
      } else {
        if (
          !buttonChannel.tag.startsWith("T") &&
          !buttonChannel.tag.startsWith("S") &&
          !buttonChannel.tag.startsWith("A")
        ) {
          const knxChannel = knxChannels.find((knxChannel) => knxChannel.knxChannelId == buttonChannel.knxChannelId);
          if (knxChannel) {
            if (rules) {
              //
              // Wenn wir ein Custom-Mapping haben
              //
              const tag = rules.mapChannelToTag(this, knxChannel);
              if (tag) {
                buttonChannel.tag = tag;
                return; // from forEach for this channel
              }
            }
          } else {
            logger.log(`fixButtonChannelTags: NO knxChannel ${buttonChannel.fullTag}`);
          }

          const { tag, index } = this.findFreeButtonTag();
          buttonChannel.tag = tag;
          buttonChannel.index = index;
        }
      }
    });

    fixDuplicateChannelTags(this);

    //////////////////////////////////////

    const buttonChannels = this.buttonChannels.sortBy("tag");
    let i = 1;
    buttonChannels.forEach((c) => {
      c.index = i++;
    });
  }

  moveToNewParent(newParent: BaseModel): boolean {
    if (newParent.className == "Floor") {
      newParent = (newParent as Floor).getRoomGeneral();
    }

    if (newParent.className == "Room" || newParent.isGroup) {
      let moved = super.moveToNewParent(newParent);

      const newPage = newParent.isRoom ? (newParent as Room).getOrCreatePage() : newParent.parentRoom?.getOrCreatePage();
      if (!newPage) return moved;

      this.channels.forEach((channel) => {
        channel.connections.forEach((connection) => {
          moved = connection.moveToNewParent(newPage) || moved;
        });
      });
      return moved;
    }

    return false;
  }

  applyConsumerChannelTypes() {
    this.buttonChannels.forEach((buttonChannel) => {
      buttonChannel.applyConsumerChannelType();
    });
  }

  applyChannelTypeProperties() {
    this.buttonChannels.forEach((buttonChannel) => {
      buttonChannel.applyChannelTypeProperty();
    });
  }

  getRockerConfig(buttonNumber: number): RockerConfig {
    if (this.baseProps.buttonBaseProps?.supportsSplitRocker) {
      return this.rockerConfigs?.[buttonNumber] || RockerConfig.none;
    } else {
      return RockerConfig.none;
    }
  }

  /** Renames an existing button area without creating channels. */
  setButtonAreaName(rowIndex: number, buttonIndex: number, name: string): void {
    const channel = this.getButtonChannel(rowIndex, buttonIndex);
    if (channel && channel.name !== name) channel.name = name;
  }

  /** Changes a rocker through the main-app channel actions and metadata update. */
  async setButtonRowSplit(rowIndex: number, split: boolean): Promise<void> {
    if (!this.baseProps.buttonBaseProps?.supportsSplitRocker) return;
    const channel = this.getButtonChannel(rowIndex, 0);
    if (!channel) return;
    const config = split ? RockerConfig.single : RockerConfig.rocker;
    const layoutMatches = this.isButtonRowSplit(rowIndex) === split;
    if (layoutMatches && this.rockerConfigs?.[rowIndex + 1] === config) return;
    await state.undoManager.runAsSingleStep("Tasteraufteilung ändern", async () => {
      if (layoutMatches) {
        this.setRockerConfig(rowIndex + 1, config);
        return;
      }
      if (this.template?.genericParams) {
        if (split) splitRockerToSingleButtons(channel);
        else mergeSingleButtonsToRocker(channel);
        await this.updateAllFromKnxMetadataImmediately();
      } else {
        await toggleButtonChannelSplit(channel);
      }
    });
  }

  /** Connects an existing request channel; the caller may supply a display label. */
  assignConsumer(tag: string, consumer: Consumer, channelName?: string): void {
    const channel = this.buttonChannels.getItemByTag(tag);
    if (!channel) return;
    const page = consumer.room?.getOrCreatePage();
    if (!page) return;
    for (const connection of channel.connections.asArray()) {
      if (connection.getPartner(channel) === consumer) return;
    }
    if (channel.connections.size === 0 && channelName) channel.name = channelName;
    channel.autoSetChannelType(consumer.channelType);
    channel.connectObject(page, consumer, ConnectionType.Button);
  }

  /** Resolves an existing channel by zero-based row and side. */
  getButtonChannel(rowIndex: number, buttonIndex: number): ButtonChannel | undefined {
    if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= (this.baseProps.buttonBaseProps?.numberOfButtonRows ?? 0) * (this.baseProps.buttonBaseProps?.numOfPages ?? 1)) return undefined;
    const tag = ButtonChannel.buildTag(rowIndex + 1, ButtonSide.both);
    if (!this.isButtonRowSplit(rowIndex)) return this.buttonChannels.getItemByTag(tag);
    return this.buttonChannels.getItemByTag(`${tag}${buttonIndex === 1 ? "R" : "L"}`)
      ?? (buttonIndex === 0 ? this.buttonChannels.getItemByTag(tag) : undefined);
  }

  /** The actual channels determine the row layout, including fixed split buttons. */
  isButtonRowSplit(rowIndex: number): boolean {
    const tag = ButtonChannel.buildTag(rowIndex + 1, ButtonSide.both);
    return !!(this.buttonChannels.getItemByTag(`${tag}L`) || this.buttonChannels.getItemByTag(`${tag}R`));
  }

  /** Button numbers follow channel tags: T01 uses index 1; index 0 stays unused. */
  setRockerConfig(buttonNumber: number, rockerConfig: RockerConfig) {
    if (this.rockerConfigs?.[buttonNumber] === rockerConfig) return rockerConfig;

    const configs = [...(this.rockerConfigs ?? [])];
    configs[buttonNumber] = rockerConfig;
    this.rockerConfigs = configs;
    return rockerConfig;
  }

  get channelsWithoutRockerValueHidden(): Array<Channel> {
    return this.channels.findAsArray((channel) => channel.buttonSide != ButtonSide.hidden);
  }

  setRockerConfigFromScript(rule: KnxRule, buttonNumber: number, params: any) {
    //
    // match against all ExtendedData
    //
    const match: RegExp = new RegExp(params.match.source, "i");
    const mapTypeToValue = params.mapTypeToValue;

    let rockerConfig = this.getRockerConfig(buttonNumber);

    const instanceData = this.knxInstanceData;

    const allModulesId = instanceData.allModuleIds();

    const buttonNumber2 = buttonNumber * 2 - 1;

    let matchFound = 0;

    const notMatchingTexts = new Array<{ input: string; match: string }>();
    const matchingTexts = new Array<{ input: string; match: string }>();

    for (const moduleId of allModulesId) {
      const extDatas = moduleId == "" ? instanceData.extData : instanceData.modules[moduleId].extData;
      for (const id in extDatas) {
        const extData = extDatas[id];
        const textEN: string = extData.info?.textEN;
        if (textEN) {
          const matches = textEN.match(match);
          if (matches && matches.length == 2) {
            matchFound++;

            if (parseInt(matches[1]) == buttonNumber2) {
              console.log("RockerProperty found for button: " + buttonNumber);
              if (rockerConfig != RockerConfig.single && rockerConfig != RockerConfig.disabled) {
                rockerConfig = RockerConfig.rocker; // no itx
              }

              const v: string = mapTypeToValue[rockerConfig];
              if (v == undefined) {
                rockerConfig = RockerConfig.rocker; // no itx
                rule.outputError("mapTypeToValue entry NOT found for: ", rockerConfig);
              }

              this.knxInstanceData.setValue(ValueLevels.interactive, `${v}`, id, moduleId, "");
              rule.output(`RockerProperty set for button:  ${buttonNumber2} to v id= ${id}, instanceId=${moduleId}`);
              matchingTexts.push({ input: textEN, match: matches[1] });
            } else {
              notMatchingTexts.push({ input: textEN, match: matches[1] });
              //// rule.output(`RockerProperty number missmatch:  b2=${buttonNumber2}  / match=${matches[1]}`);
            }
          }
        }
      }

      if (!matchFound) {
        rule.outputError("No Property is matching: ", match.source);
        rule.outputError(`For Rocker ${buttonNumber} (Buttons ${buttonNumber2}/${buttonNumber2 + 1})`);
      } else {
        if (isDevelopmentEnvironment()) {
          rule.output(`RegEx: ${match.toString()}`);
          rule.output(`Matching Properties found:`);
          matchingTexts.forEach((item) => {
            rule.output(`Property text: ${item.input} => Match: ${item.match}`);
          });
        }
      }
    }
  }
  getCableType(): CableType | undefined {
    let res: CableType | undefined;
    // eventualy set in button or settings but now its per default YCYM 2x2x0.8
    state.cableTypes.forEach((cableType) => {
      if (cableType.tag == "C4.08") {
        res = cableType;
      }
    });
    return res;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    return await this.treeViewModel?.handleDrop2(mode, obj);
  }

  get connectedObjectsInOtherRooms(): Array<Consumer | Button> {
    const res = new Array<Consumer | Button>();
    const ownRoom = this.room;
    if (ownRoom) {
      this.buttonChannels.forEach((buttonChannel) => {
        buttonChannel.connections.forEach((con) => {
          if (con.type == ConnectionType.Button) {
            const partnerConsumer = con.getPartner(buttonChannel) as Consumer;

            if (partnerConsumer?.isConsumer) {
              const partnerRoom = partnerConsumer.room;
              if (partnerRoom && partnerRoom != ownRoom) {
                res.push(partnerConsumer);
              }
            }
          }
        });
      });
    }

    return res;
  }
}
